import { Box, Flex, Spinner } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import { MoneyInput } from './MaskedInput'
import { Tooltip } from './Tooltip'

/**
 * Money counterpart of DateBlurInput: edits inline in a table cell and persists
 * on blur. Clearing the field saves null, which callers use to drop an override.
 *
 * A máscara e o `R$` são do `<MoneyInput>`; o que é deste componente é só o salvamento
 * automático no blur — ele guardava a sua própria conversão de dígitos para número e o seu
 * próprio `R$` embutido no valor, que eram a quarta grafia de dinheiro do painel.
 */
export default function MoneyBlurInput({
  initialValue,
  onSave,
  ariaLabel,
  placeholder,
  isDisabled = false
}: {
  initialValue: number | null
  onSave: (value: number | null) => Promise<void> | void
  ariaLabel?: string
  placeholder?: string
  isDisabled?: boolean
}) {
  const [value, setValue] = useState(initialValue)
  const [saving, setSaving] = useState(false)
  const savedRef = useRef(initialValue)

  useEffect(() => {
    setValue(initialValue)
    savedRef.current = initialValue
  }, [initialValue])

  const handleBlur = async () => {
    if (value === savedRef.current) return
    setSaving(true)
    try {
      await onSave(value)
      // Only mark as saved once persistence succeeds, so a failed save retries on the next blur.
      savedRef.current = value
    } catch {
      // Keep savedRef unchanged to allow retry; the caller surfaces the error toast.
    } finally {
      setSaving(false)
    }
  }

  return (
    <Flex alignItems="center" gap={1} w="160px">
      <MoneyInput
        size="xs"
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={value}
        onChange={setValue}
        onBlur={handleBlur}
        isDisabled={isDisabled || saving}
      />
      <Box w="14px" flexShrink={0}>
        {saving && (
          <Tooltip label="Salvando..." isOpen>
            <Spinner size="xs" color="blue.500" />
          </Tooltip>
        )}
      </Box>
    </Flex>
  )
}
